import { useCallback, useEffect, useRef } from 'react'
import { useBlocker, type BlockerFunction } from 'react-router-dom'
import { showConfirmModal } from '../components/ui/ConfirmDialog'

const UNSAVED_CODE_SHEET_HISTORY_GUARD_KEY = '__codeSheetUnsavedGuard'
const UNSAVED_CODE_SHEET_WARNING = 'Changes made will not be saved unless you select Save Changes. Stay on this page to keep your updates, or exit without saving to discard them.'
const BEFORE_UNLOAD_WARNING = 'Changes made will not be saved unless you select Save Changes.'

export type UseUnsavedCodeSheetNavigationGuardOptions = {
  hasUnsavedChanges: boolean
  onDiscardChanges: () => void
}

export function useUnsavedCodeSheetNavigationGuard({
  hasUnsavedChanges,
  onDiscardChanges,
}: UseUnsavedCodeSheetNavigationGuardOptions) {
  const hasUnsavedChangesRef = useRef(false)
  const promptOpenRef = useRef(false)
  const browserBackGuardIdRef = useRef<string | null>(null)
  const suppressNextBrowserBackGuardPopRef = useRef(false)
  hasUnsavedChangesRef.current = hasUnsavedChanges

  const confirmExit = useCallback(() => showConfirmModal({
    title: 'Unsaved CodeSheet changes',
    description: UNSAVED_CODE_SHEET_WARNING,
    confirmText: 'Exit without saving',
    cancelText: 'Stay on page',
  }), [])

  const shouldBlockNavigation = useCallback<BlockerFunction>(({ currentLocation, nextLocation }) => {
    if (!hasUnsavedChangesRef.current) return false

    return (
      currentLocation.pathname !== nextLocation.pathname ||
      currentLocation.search !== nextLocation.search ||
      currentLocation.hash !== nextLocation.hash
    )
  }, [])

  const navigationBlocker = useBlocker(shouldBlockNavigation)

  const allowNavigationWithoutPrompt = useCallback(() => {
    hasUnsavedChangesRef.current = false
  }, [])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault()
        event.returnValue = BEFORE_UNLOAD_WARNING
      }
    }

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload)
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  useEffect(() => {
    if (!hasUnsavedChanges || typeof window === 'undefined') return

    if (!browserBackGuardIdRef.current) {
      browserBackGuardIdRef.current = `code-sheet-unsaved-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    }

    const guardId = browserBackGuardIdRef.current
    const getCurrentHistoryState = (): Record<string, unknown> => {
      const currentState = window.history.state
      return currentState && typeof currentState === 'object'
        ? { ...currentState }
        : {}
    }

    const pushBrowserBackGuardEntry = () => {
      const currentState = getCurrentHistoryState()
      if (currentState[UNSAVED_CODE_SHEET_HISTORY_GUARD_KEY] === guardId) return

      window.history.pushState({
        ...currentState,
        [UNSAVED_CODE_SHEET_HISTORY_GUARD_KEY]: guardId,
      }, '', window.location.href)
    }

    pushBrowserBackGuardEntry()

    const handleBrowserBackPopState = () => {
      if (suppressNextBrowserBackGuardPopRef.current) {
        suppressNextBrowserBackGuardPopRef.current = false
        return
      }

      if (!hasUnsavedChangesRef.current) return

      pushBrowserBackGuardEntry()

      if (promptOpenRef.current) return
      promptOpenRef.current = true

      void (async () => {
        try {
          const shouldExit = await confirmExit()

          if (shouldExit) {
            hasUnsavedChangesRef.current = false
            onDiscardChanges()
            suppressNextBrowserBackGuardPopRef.current = true
            window.history.go(-2)
          }
        } catch (error) {
          console.error('Failed to confirm unsaved CodeSheet browser back navigation', error)
        } finally {
          promptOpenRef.current = false
        }
      })()
    }

    window.addEventListener('popstate', handleBrowserBackPopState)

    return () => {
      window.removeEventListener('popstate', handleBrowserBackPopState)
    }
  }, [confirmExit, hasUnsavedChanges, onDiscardChanges])

  useEffect(() => {
    if (
      navigationBlocker.state !== 'blocked' ||
      !hasUnsavedChanges ||
      promptOpenRef.current
    ) {
      return
    }

    const blocker = navigationBlocker
    promptOpenRef.current = true

    void (async () => {
      try {
        const shouldExit = await confirmExit()

        if (shouldExit) {
          hasUnsavedChangesRef.current = false
          onDiscardChanges()
          blocker.proceed?.()
        } else {
          blocker.reset?.()
        }
      } catch (error) {
        console.error('Failed to confirm unsaved CodeSheet navigation', error)
        blocker.reset?.()
      } finally {
        promptOpenRef.current = false
      }
    })()
  }, [confirmExit, hasUnsavedChanges, navigationBlocker, onDiscardChanges])

  return { allowNavigationWithoutPrompt }
}
